import { useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router'
import { Loader2, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/logger'
import { Button } from '@/components/ui/button'
import { SidebarContent } from '@/components/ui/sidebar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ModelPicker } from '@/components/generate/ModelPicker'
import { AspectRatioPicker } from '@/components/generate/AspectRatioPicker'
import { useGenerateWorkspace } from '@/context/GenerateWorkspaceContext'
import {
  buildActorPrompt,
  BUST_SIZE_OPTIONS,
  BEARD_STYLE_OPTIONS,
  FACE_SHAPE_OPTIONS,
  EYE_COLOR_OPTIONS,
  SKIN_TONE_PALETTE,
  HAIR_COLOR_PALETTE,
  type ActorCharacterForm,
} from '@/lib/buildActorPrompt'

const AGE_GROUP_OPTIONS = [
  'Teenager (16-19)',
  'Young Adult (20-29)',
  'Adult (30-39)',
  'Middle Age (40-50)',
  'Young Senior (50-70)',
  'Seniors (70+)',
] as const

const ETHNICITY_OPTIONS = [
  'South Asian',
  'Southeast Asian',
  'East Asian',
  'White/Caucasian',
  'African',
  'African American',
  'Hispanic/Latino',
  'Middle Eastern',
  'Pacific Islander',
  'Other',
] as const

const FEMALE_BODY_SHAPE_OPTIONS = [
  'Slim',
  'Athletic',
  'Curvy (Hourglass)',
  'Pear',
  'Plus Size',
] as const
const MALE_BODY_SHAPE_OPTIONS = [
  'Slim',
  'Athletic',
  'Muscular',
  'Average',
  'Dad Bod',
  'Plus Size',
] as const

const HAIR_LENGTH_OPTIONS = [
  'Bald/Shaved',
  'Buzz Cut',
  'Short',
  'Chin-length (Bob)',
  'Medium',
  'Long',
  'Very Long',
] as const

const HAIR_STYLE_OPTIONS = [
  'Straight',
  'Wavy',
  'Curly',
  'Coily',
  'Pixie Cut',
  'Bob Cut',
  'Braids',
  'Locs',
  'Ponytail',
  'Bun/Updo',
  'Fringe/Bangs',
] as const

const EXPRESSION_OPTIONS = [
  'Neutral',
  'Smile',
  'Big Laugh',
  'Surprised',
  'Serious',
  'Confident',
  'Mischievous/Smirking',
  'Thoughtful',
  'Confused/Skeptical',
] as const

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  )
}

function ColorSwatch({
  palette,
  selected,
  onSelect,
}: {
  palette: typeof SKIN_TONE_PALETTE
  selected: string
  onSelect: (hex: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {palette.map((color) => (
        <button
          key={color.hex}
          type="button"
          title={color.label}
          onClick={() => onSelect(selected === color.hex ? '' : color.hex)}
          style={{ backgroundColor: color.hex }}
          className={`h-6 w-6 rounded-full border-2 transition-all focus:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
            selected === color.hex
              ? 'scale-110 border-primary shadow-md'
              : 'border-border/60 hover:border-foreground/50'
          } ${color.hex === '#FFFFFF' ? 'border-border' : ''}`}
        />
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ActorsFormSidebar() {
  const {
    providerName,
    keysLoading,
    keyFetching,
    hasKey,
    modelsLoading,
    models,
    modelId,
    setModelId,
    setPrompt,
    aspectRatio,
    setAspectRatio,
    busy,
    handleGenerate,
    actorInputMode,
    setActorInputMode,
    actorForm,
    setActorForm,
    actorFreeformPrompt,
    setActorFreeformPrompt,
  } = useGenerateWorkspace()

  const recommendedId = models.find((m) => m.id.startsWith('imagen-4.0-fast'))?.id

  // Default to Imagen 4 Fast once when models first load; never override a preset's model choice.
  const initializedRef = useRef(false)
  useEffect(() => {
    if (recommendedId && !initializedRef.current) {
      initializedRef.current = true
      // Only set recommended model if no model is pre-selected (e.g. from a preset load)
      if (!modelId) {
        setModelId(recommendedId)
      }
    }
  }, [recommendedId, setModelId, modelId])

  // Keep context prompt in sync so handleGenerate() always reads the current value
  const builtPrompt = useMemo(
    () => (actorInputMode === 'form' ? buildActorPrompt(actorForm) : actorFreeformPrompt),
    [actorInputMode, actorForm, actorFreeformPrompt],
  )
  useEffect(() => {
    setPrompt(builtPrompt)
  }, [builtPrompt, setPrompt])

  function setField<K extends keyof ActorCharacterForm>(key: K, value: ActorCharacterForm[K]) {
    setActorForm({ ...actorForm, [key]: value })
  }

  function toggleEthnicity(value: string) {
    setActorForm({
      ...actorForm,
      ethnicities: actorForm.ethnicities.includes(value)
        ? actorForm.ethnicities.filter((e) => e !== value)
        : [...actorForm.ethnicities, value],
    })
  }

  const isFemale = actorForm.gender === 'Female'
  const isMale = actorForm.gender === 'Male'
  const bodyShapeOptions = isMale ? MALE_BODY_SHAPE_OPTIONS : FEMALE_BODY_SHAPE_OPTIONS

  const isLoading = keysLoading || keyFetching || (modelsLoading && models.length === 0)
  const canGenerate =
    hasKey &&
    !keyFetching &&
    !busy &&
    (actorInputMode === 'prompt' ? actorFreeformPrompt.trim().length > 0 : actorForm.gender !== '')

  return (
    <SidebarContent>
      <div
        className={cn(
          'flex flex-col gap-2 p-4 transition-opacity duration-150',
          isLoading && 'pointer-events-none select-none opacity-0',
        )}
      >
        {!hasKey && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-400">
            No {providerName} key.{' '}
            <Link to="/apps/settings" className="underline underline-offset-2 hover:text-amber-300">
              Add in Settings
            </Link>
          </div>
        )}
        <ModelPicker
          models={models}
          value={modelId}
          onChange={setModelId}
          recommendedId={recommendedId}
        />

        {/* Mode toggle */}
        <div className="flex rounded-lg bg-muted p-0.5 gap-0.5">
          <button
            type="button"
            onClick={() => setActorInputMode('form')}
            className={`flex-1 rounded-md py-1 text-xs font-semibold transition-colors ${
              actorInputMode === 'form'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Prompt Builder
          </button>
          <button
            type="button"
            onClick={() => setActorInputMode('prompt')}
            className={`flex-1 rounded-md py-1 text-xs font-semibold transition-colors ${
              actorInputMode === 'prompt'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Prompt
          </button>
        </div>

        {/* Prompt mode */}
        {actorInputMode === 'prompt' && (
          <div className="flex flex-col gap-1">
            <textarea
              value={actorFreeformPrompt}
              onChange={(e) => setActorFreeformPrompt(e.target.value)}
              placeholder="Describe the actor you want to generate (appearance, style, mood)..."
              rows={6}
              className="w-full resize-y rounded-lg border border-border bg-input/30 px-2.5 py-1.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        )}

        {/* Form mode */}
        {actorInputMode === 'form' && (
          <>
            <SectionHeader>Basics</SectionHeader>

            {/* Character Name */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Character Name</p>
              <input
                type="text"
                value={actorForm.characterName}
                onChange={(e) => setField('characterName', e.target.value)}
                placeholder="e.g. Mia"
                className="h-8 w-full rounded-md border border-border bg-input/30 px-2.5 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            {/* Gender */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">
                Gender <span className="text-destructive">*</span>
              </p>
              <div className="flex gap-1.5">
                {(['Female', 'Male'] as const).map((g) => (
                  <Button
                    key={g}
                    type="button"
                    size="sm"
                    variant={actorForm.gender === g ? 'default' : 'outline'}
                    onClick={() => {
                      const next = actorForm.gender === g ? ('' as const) : g
                      setActorForm({
                        ...actorForm,
                        gender: next,
                        bustSize: '',
                        bodyShape: '',
                        beardStyle: '',
                      })
                    }}
                    className="flex-1 text-xs"
                  >
                    {g}
                  </Button>
                ))}
              </div>
            </div>

            {/* Age Group */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Age Group</p>
              <Select value={actorForm.ageGroup} onValueChange={(v) => setField('ageGroup', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select age group..." />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUP_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ethnicity */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Ethnicity</p>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                {ETHNICITY_OPTIONS.map((eth) => (
                  <label key={eth} className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={actorForm.ethnicities.includes(eth)}
                      onChange={() => toggleEthnicity(eth)}
                      className="h-3.5 w-3.5 shrink-0 rounded border-border bg-input accent-primary"
                    />
                    <span className="text-xs leading-tight text-foreground">{eth}</span>
                  </label>
                ))}
              </div>
            </div>

            <SectionHeader>Body &amp; Skin</SectionHeader>

            {/* Skin Tone */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Skin Tone</p>
              <ColorSwatch
                palette={SKIN_TONE_PALETTE}
                selected={actorForm.skinTone}
                onSelect={(hex) => setField('skinTone', hex)}
              />
              {actorForm.skinTone && (
                <p className="text-xs text-muted-foreground">
                  {SKIN_TONE_PALETTE.find((c) => c.hex === actorForm.skinTone)?.label}
                </p>
              )}
            </div>

            {/* Bust Size + Body Shape (or Body Shape full-width for non-female) */}
            {isFemale ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-muted-foreground">Bust Size</p>
                  <Select value={actorForm.bustSize} onValueChange={(v) => setField('bustSize', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Auto" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUST_SIZE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-xs">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-muted-foreground">Body Shape</p>
                  <Select
                    value={actorForm.bodyShape}
                    onValueChange={(v) => setField('bodyShape', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Auto" />
                    </SelectTrigger>
                    <SelectContent>
                      {bodyShapeOptions.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-xs">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Body Shape</p>
                <Select value={actorForm.bodyShape} onValueChange={(v) => setField('bodyShape', v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {bodyShapeOptions.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <SectionHeader>Hair</SectionHeader>

            {/* Hair Color */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Hair Color</p>
              <ColorSwatch
                palette={HAIR_COLOR_PALETTE}
                selected={actorForm.hairColor}
                onSelect={(hex) => setField('hairColor', hex)}
              />
              {actorForm.hairColor && (
                <p className="text-xs text-muted-foreground">
                  {HAIR_COLOR_PALETTE.find((c) => c.hex === actorForm.hairColor)?.label}
                </p>
              )}
            </div>

            {/* Hair Length + Hair Style */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Hair Length</p>
                <Select
                  value={actorForm.hairLength}
                  onValueChange={(v) => setField('hairLength', v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {HAIR_LENGTH_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Hair Style</p>
                <Select value={actorForm.hairStyle} onValueChange={(v) => setField('hairStyle', v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {HAIR_STYLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Beard Style — male only */}
            {isMale && (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Beard Style</p>
                <Select
                  value={actorForm.beardStyle}
                  onValueChange={(v) => setField('beardStyle', v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Clean-shaven" />
                  </SelectTrigger>
                  <SelectContent>
                    {BEARD_STYLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <SectionHeader>Face</SectionHeader>

            {/* Eye Color + Face Shape */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Eye Color</p>
                <Select value={actorForm.eyeColor} onValueChange={(v) => setField('eyeColor', v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {EYE_COLOR_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">Face Shape</p>
                <Select value={actorForm.faceShape} onValueChange={(v) => setField('faceShape', v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {FACE_SHAPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Expression */}
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Expression</p>
              <Select value={actorForm.expression} onValueChange={(v) => setField('expression', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Auto" />
                </SelectTrigger>
                <SelectContent>
                  {EXPRESSION_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <AspectRatioPicker
          value={aspectRatio}
          onChange={setAspectRatio}
          options={['1:1', '16:9', '9:16']}
        />

        <Button
          variant="cta"
          onClick={() => {
            logger.debug('[AI Actors] prompt →', builtPrompt)
            void handleGenerate()
          }}
          disabled={!canGenerate}
          className="w-full"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating…
            </>
          ) : (
            'Generate'
          )}
        </Button>

        <Link
          to="/apps/settings"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Settings className="h-3.5 w-3.5" />
          Manage API Keys
        </Link>
      </div>
    </SidebarContent>
  )
}
